import styled, { theme } from '@nobia/zeus-components/lib/styled'
import rem from '@nobia/zeus-components/lib/styled/rem'
import PropTypes from 'prop-types'
import React, { forwardRef, useCallback, useEffect, useState } from 'react'

import { useTranslation } from '../../utils/useTranslation'
import { OutlineButton, SolidButton } from '../button/Button'
import PostalCodeSelector from '../postalCodeSelector/PostalCodeSelector'
import { H1 } from './common'

const Wrapper = styled.div`
  text-align: left;
  max-width: ${rem(500)};
`
const Label = styled.label`
  font-size: ${rem(16)};
`
const Input = styled.input`
  background-color: #fff;
  width: 100%;
  height: ${theme('forms.height')};
  padding: 0 ${theme('forms.padding')};
  border-radius: ${(p) => rem(p.theme.forms.borderRadius)};
  border: ${rem(1)} solid ${theme('forms.colors.border')};
  font-weight: ${theme('forms.fontWeight')};
  font-size: ${theme('forms.fontSize')};
  font-family: ${theme('forms.fontFamily')};
  margin: ${rem(3)} 0 ${rem(14)};
  :-webkit-autofill,
  :-webkit-autofill:hover,
  :-webkit-autofill:focus,
  :-webkit-autofill:active {
    box-shadow: 0 0 0 30px #fff inset !important;
  }
`

const ErrorText = styled.span`
  color: ${(p) => p.theme.colors.error};
`

const CancelButton = styled(OutlineButton)`
  width: 100%;
  line-height: ${rem(32)};
  padding: ${rem(7)};
`

const Row = styled.div`
  display: inline-block;
  margin: ${rem(6)} 0;
  width: 100%;
`
const Flex = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 30px;
  height: 46px;
  gap: 20px;
  @media (min-width: 400px) {
    flex-wrap: nowrap;
  }
`

export const validateName = (name) => {
  if (!name) return false
  const hasNumbers = /[0-9]/
  const hasSpecialCharacters = /[!"#€%&/()+=?`´©@£$∞§|≈±,*<>;:]/
  const hasLeadingOrTrailingSpace = /^\s|\s$/
  const nameIsLongerThanLongestNameInHistory = name.length > 35
  if (hasNumbers.test(name)) return false
  if (hasSpecialCharacters.test(name)) return false
  if (hasLeadingOrTrailingSpace.test(name)) return false
  if (nameIsLongerThanLongestNameInHistory) return false
  return true
}

const defaultForm = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  houseNumber: '',
  addressLine1: '',
  addressLine2: '',
  addressLine3: '',
  city: '',
  country: '',
  postalCode: '',
}

const EditUserDetails = forwardRef(({ onSubmit, onClose, user }, ref) => {
  const [form, setForm] = useState(defaultForm)
  const [formError, setFormError] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const { t } = useTranslation('app.pages.user-settings.edit-details')

  const submit = useCallback(() => {
    setSubmitted(true)
    const errors = Object.keys(formError).filter((k) => formError[k])
    if (errors.length) {
      return
    }
    setSubmitting(true)
    onSubmit({ ...form })
  }, [formError, form])

  useEffect(() => {
    if (submitting) {
      return
    }

    const formData = { ...defaultForm }

    if (user) {
      Object.keys(user)
        .filter((key) => user[key])
        .forEach((key) => {
          formData[key] = user[key]
        })
    }

    setForm(formData)

    setFormError({
      firstName: !validateName(user.firstName),
      lastName: !validateName(user.lastName),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const parseHouseNumber = useCallback((line) => {
    const split = line.split(' ')
    const number = +split[0]
    const address = split.slice(1).join(' ')
    if (number) {
      return { number, address }
    }
    return { address: line }
  }, [])

  const onAddressChange = useCallback(
    ({ city, countryName, line1, line2, line3, postalCode }) => {
      const addressFields = {
        houseNumber: '',
        addressLine1: line1,
        addressLine2: line2,
        addressLine3: line3,
        city,
        country: countryName,
        postalCode,
      }
      const address1 = parseHouseNumber(line1)
      if (address1.number) {
        addressFields.houseNumber = address1.number.toString()
        addressFields.addressLine1 = address1.address
      }
      setForm({
        ...form,
        ...addressFields,
      })
    },
    [form, parseHouseNumber]
  )

  const onChange = useCallback(
    (e) => {
      e.preventDefault()
      setForm({ ...form, [e.target.name]: e.target.value })
      if (e.target.name === 'firstName' || e.target.name === 'lastName') {
        const newErrors = {
          ...formError,
          [e.target.name]: !validateName(e.target.value),
        }
        setFormError(newErrors)
      }
    },
    [formError, form]
  )

  return (
    <Wrapper>
      <H1>{t('heading')}</H1>
      <Row>
        <Label>{t('first-name')}</Label>
        <Input
          data-cs-mask
          value={form.firstName}
          name="firstName"
          onChange={onChange}
        />
        {submitted && formError.firstName && (
          <ErrorText>{t('first-name.error')}</ErrorText>
        )}
      </Row>
      <Row>
        <Label>{t('last-name')}</Label>
        <Input
          data-cs-mask
          value={form.lastName}
          name="lastName"
          onChange={onChange}
        />
        {submitted && formError.lastName && (
          <ErrorText>{t('last-name.error')}</ErrorText>
        )}
      </Row>
      <Row>
        <Label>{t('phone')}</Label>
        <Input
          data-cs-mask
          value={form.phoneNumber}
          name="phoneNumber"
          onChange={onChange}
        />
      </Row>
      <Row>
        <Label>{t('address')}</Label>
        <PostalCodeSelector
          address={{
            city: form.city,
            country: form.countryName,
            addressLine1:
              (form.houseNumber ? `${form.houseNumber} ` : '') +
              form.addressLine1,
            addressLine2: form.addressLine2,
            addressLine3: form.addressLine3,
          }}
          onAddressChange={onAddressChange}
          postalCode={form.postalCode}
          boundaryRef={ref}
        />
      </Row>
      <Flex>
        <SolidButton fullWidth disabled={submitting} onClick={submit}>
          {submitting ? t('confirm-saving') : t('confirm')}
        </SolidButton>
        <CancelButton onClick={onClose}>{t('cancel')}</CancelButton>
      </Flex>
    </Wrapper>
  )
})

EditUserDetails.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    postalCode: PropTypes.string,
    city: PropTypes.string,
    houseNumber: PropTypes.string,
    addressLine1: PropTypes.string,
    addressLine2: PropTypes.string,
    addressLine3: PropTypes.string,
    country: PropTypes.string,
  }).isRequired,
}

export default EditUserDetails
